/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import type { PayloadAction, Reducer } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { pluginsActions } from '@opengeoweb/plugins';
import type { syncGroupsTypes } from '@opengeoweb/store';
import { syncGroupsActions } from '@opengeoweb/store';
import type {
  WorkspaceAddViewPayload,
  WorkspaceDeleteViewPayload,
  WorkspaceState,
  WorkspaceSetPresetPayload,
  WorkspaceSetPreventCloseViewPayload,
  WorkspaceUpdateViewPayload,
  WorkspaceUpdateViewsPayload,
  WorkspaceErrorWorkspacePayload,
  WorkspaceFetchWorkspacePayload,
  WorkspaceFetchWorkspaceViewPresetPayload,
  WorkspaceSaveWorkspacePresetPayload,
  WorkspaceViewType,
  WorkspaceSetNewViewPayload,
  WorkspaceChangeToEmptyMapWorkspacePayload,
  WorkspaceViewsFromBE,
} from './types';
import { WorkspacePresetAction } from './types';
import { getViewType, makeListOfViewIds } from './utils';
import { viewPresetActions } from '../viewPresets/reducer';
import type {
  SelectViewPresetPayload,
  SetActiveViewPresetIdPayload,
  ViewPreset,
} from '../viewPresets/types';
import { PresetAction } from '../viewPresets/types';
import { workspaceListActions } from '../workspaceList/reducer';
import { getEmptyMapWorkspace } from '../workspaceList/utils';

export const initialState: WorkspaceState = {
  id: '',
  title: '',
  views: {
    byId: {},
    allIds: [],
  },
  mosaicNode: undefined!,
  isLoading: false,
  error: undefined,
  hasChanges: false,
};

const workspacePresetChangeActions = [
  viewPresetActions.selectViewPreset,
  viewPresetActions.setActiveViewPresetId,
  syncGroupsActions.syncGroupRemoveGroup,
  syncGroupsActions.syncGroupAddGroup,
  syncGroupsActions.syncGroupToggleIsTimeScrollingEnabled,
  syncGroupsActions.updateLinkedMap,
  pluginsActions.setGeoWebPluginState,
];

const slice = createSlice({
  initialState,
  name: 'workspace',
  reducers: {
    addWorkspaceView: (
      draft,
      action: PayloadAction<WorkspaceAddViewPayload>,
    ) => {
      const newId = action.payload.id;
      draft.views.allIds.push(newId);
      draft.views.byId![newId] = {
        componentType: action.payload.componentType,
        initialProps: action.payload.initialProps,
      };
      draft.hasChanges = true;
      draft.viewType = getViewType(
        draft.views.allIds.length,
        action.payload.componentType,
      );
    },
    setNewWorkspaceView: (
      _draft,
      _action: PayloadAction<WorkspaceSetNewViewPayload>,
    ) => {},
    teardownComplete: () => {
      // Signal-only action for listener coordination
    },
    deleteWorkspaceView: (
      draft,
      action: PayloadAction<WorkspaceDeleteViewPayload>,
    ) => {
      const { id } = action.payload;
      draft.views.allIds = draft.views.allIds.filter(
        (viewId: string) => viewId !== id,
      );
      delete draft.views.byId![id];
      draft.hasChanges = true;

      // Determine new viewType
      const firstViewId = draft.views.allIds[0];
      const viewComponentType =
        draft.views.byId![firstViewId] !== undefined
          ? draft.views.byId![firstViewId].componentType
          : '';
      const newViewType = getViewType(
        draft.views.allIds.length,
        viewComponentType,
      );
      if (newViewType !== undefined) {
        draft.viewType = newViewType;
      } else {
        delete draft.viewType;
      }
    },
    updateWorkspaceViews: (
      draft,
      action: PayloadAction<WorkspaceUpdateViewsPayload>,
    ) => {
      draft.mosaicNode = action.payload.mosaicNode;

      const mosaicViewIds: string[] = makeListOfViewIds(
        action.payload.mosaicNode,
      );

      /* Delete all view id's which are still in the redux state but not in the mosaic */
      draft.views.allIds.forEach((id: string) => {
        if (!mosaicViewIds.includes(id)) {
          draft.views.allIds = draft.views.allIds.filter(
            (viewId: string) => viewId !== id,
          );
          delete draft.views.byId![id];
        }
      });
      draft.hasChanges = true;

      // Determine new viewType
      const firstViewId = draft.views.allIds[0];
      const viewComponentType =
        draft.views.byId![firstViewId] !== undefined
          ? draft.views.byId![firstViewId].componentType
          : '';
      const newViewType = getViewType(
        draft.views.allIds.length,
        viewComponentType,
      );
      if (newViewType !== undefined) {
        draft.viewType = newViewType;
      } else {
        delete draft.viewType;
      }
    },

    updateWorkspaceView: (
      draft,
      action: PayloadAction<WorkspaceUpdateViewPayload>,
    ) => {
      const {
        payload: { mosaicNodeId, viewPreset },
      } = action;
      draft.views.byId![mosaicNodeId] = viewPreset;
    },

    changePreset: (draft, action: PayloadAction<WorkspaceSetPresetPayload>) => {
      const { workspacePreset } = action.payload;
      // reset state on change preset
      draft.views.allIds = [];
      draft.views.byId = {};
      draft.mosaicNode = '';
      draft.id = '';
      draft.title = workspacePreset.title;
      draft.abstract = workspacePreset.abstract;
      draft.description = workspacePreset.description;
      draft.labels = workspacePreset.labels;
      draft.scope = workspacePreset.scope;
      draft.username = workspacePreset.username;
      draft.syncGroups = [];
      draft.username = workspacePreset.username;
    },

    changeToEmptyMapWorkspace: (
      draft,

      action: PayloadAction<WorkspaceChangeToEmptyMapWorkspacePayload>,
    ) => {
      return draft;
    },

    setPreset: (draft, action: PayloadAction<WorkspaceSetPresetPayload>) => {
      // do not change anything when the preset is already set
      if (draft.id === action.payload.workspacePreset.id) {
        return draft;
      }
      const newViews = cloneDeep(action.payload.workspacePreset.views);

      // check if any viewIds are duplicate within the selected preset
      const duplicateIdWithinPreset = newViews.allIds.filter(
        (item, index) => newViews.allIds.indexOf(item) !== index,
      );

      if (duplicateIdWithinPreset.length) {
        console.warn('Invalid preset: duplicate viewId found.');
        return initialState;
      }

      draft.views = newViews;
      if (action.payload.workspacePreset.syncGroups) {
        draft.syncGroups = cloneDeep(action.payload.workspacePreset.syncGroups);
      } else {
        draft.syncGroups = [];
      }
      draft.id = action.payload.workspacePreset.id;
      draft.title = action.payload.workspacePreset.title;
      draft.mosaicNode = action.payload.workspacePreset.mosaicNode;
      draft.minimumPaneSizePercentage =
        action.payload.workspacePreset.minimumPaneSizePercentage;
      draft.hasChanges = false;
      draft.scope = action.payload.workspacePreset.scope;
      draft.viewType = action.payload.workspacePreset.viewType;
      draft.isTimeScrollingEnabled =
        action.payload.workspacePreset.isTimeScrollingEnabled;
      draft.labels = action.payload.workspacePreset.labels;
      draft.description = action.payload.workspacePreset.description;
      draft.username = action.payload.workspacePreset.username;

      return draft;
    },
    setPreventCloseView: (
      draft,
      action: PayloadAction<WorkspaceSetPreventCloseViewPayload>,
    ) => {
      const { viewId, shouldPreventClose } = action.payload;
      draft.views.byId![viewId] = {
        ...draft.views.byId![viewId],
        shouldPreventClose,
      };
    },

    // workspaces
    fetchWorkspace: (
      draft,
      _action: PayloadAction<WorkspaceFetchWorkspacePayload>,
    ) => {
      draft.isLoading = true;
      draft.error = undefined;
    },
    loadedWorkspace: (draft, action) => {
      draft.isLoading = false;

      const ws = action.payload?.workspace;
      if (!ws) {
        return;
      }

      draft.title = ws.title;
      draft.abstract = ws.abstract;
      draft.labels = ws.labels;
      draft.description = ws.description;
      draft.username =
        typeof ws.username === 'string' ? ws.username : undefined;

      const wsViews = ws.views;

      let viewsArray: WorkspaceViewsFromBE[] = [];
      if (Array.isArray(wsViews)) {
        viewsArray = wsViews as WorkspaceViewsFromBE[];
      } else if (wsViews?.byId && Array.isArray(wsViews.allIds)) {
        viewsArray = wsViews.allIds.map((id: string) => {
          const viewData = wsViews.byId[id] ?? {};
          return {
            mosaicNodeId: id,
            title: viewData.title,
            componentType: viewData.componentType,
            initialProps: viewData.initialProps,
            viewPresetId: viewData.viewPresetId,
            originalViewPresetId: viewData.originalViewPresetId,
            abstract: viewData.abstract,
            scope: viewData.scope,
          } as WorkspaceViewsFromBE;
        });
      } else {
        viewsArray = [];
      }

      draft.views = { byId: {}, allIds: [] };

      viewsArray.forEach((view) => {
        const key = view.mosaicNodeId;
        if (!key) {
          return;
        }

        draft.views.byId[key] = {
          id: key,
          mosaicNodeId: key,
          title:
            typeof view.title === 'string' && view.title.trim().length
              ? view.title
              : key,
          componentType: view.componentType ?? 'Map',
          initialProps: view.initialProps ?? {},
        };
        draft.views.allIds.push(key);
      });

      draft.hasChanges = false;
      draft.error = undefined;
    },

    errorWorkspace: (
      draft,
      action: PayloadAction<WorkspaceErrorWorkspacePayload>,
    ) => {
      const { error } = action.payload;
      draft.isLoading = false;
      draft.error = error;
    },
    fetchWorkspaceViewPreset: (
      _draft,
      _action: PayloadAction<WorkspaceFetchWorkspaceViewPresetPayload>,
    ) => {},
    saveWorkspacePreset: (
      draft,
      _action: PayloadAction<WorkspaceSaveWorkspacePresetPayload>,
    ) => {
      draft.isLoading = true;
      draft.error = undefined;
    },
    markWorkspaceChanged: (draft) => {
      draft.hasChanges = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        workspaceListActions.onSuccessWorkspacePresetAction,
        (draft, action) => {
          if (
            action.payload.action === WorkspacePresetAction.SAVE_AS ||
            action.payload.action ===
              WorkspacePresetAction.SAVE_SYSTEM_PRESET ||
            action.payload.action ===
              WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS ||
            action.payload.action === WorkspacePresetAction.SAVE
          ) {
            const scope = action.payload.scope || 'user';
            const username =
              'username' in action.payload &&
              typeof action.payload.username === 'string'
                ? action.payload.username
                : undefined;
            draft.id = action.payload.workspaceId;
            draft.title = action.payload.title;
            draft.abstract = action.payload.abstract;
            draft.description = action.payload.description;
            draft.labels = action.payload.labels;
            draft.hasChanges = false;
            draft.scope = scope;
            draft.username = username;

            // Update all views to contain the correct username and scope
            draft.views.allIds.forEach((viewId) => {
              draft.views.byId[viewId].scope = scope;
              draft.views.byId[viewId].username = username;
            });
          }

          if (
            (action.payload.action === WorkspacePresetAction.EDIT ||
              action.payload.action === WorkspacePresetAction.EDIT_TITLE) &&
            action.payload.workspaceId === draft.id
          ) {
            // Only EDIT updates ws title/abstract
            if (action.payload.action === WorkspacePresetAction.EDIT) {
              draft.title = action.payload.title;
              draft.abstract = action.payload.abstract;
              draft.labels = action.payload.labels;
              draft.description = action.payload.description;
            }

            const changedViews = action.payload.views as
              | { mosaicNodeId?: string; title?: string }[]
              | undefined;

            if (changedViews && draft.views?.byId) {
              changedViews.forEach((view) => {
                const key = view.mosaicNodeId;
                if (key && draft.views.byId[key]) {
                  draft.views.byId[key].title =
                    view.title ?? draft.views.byId[key].title;
                }
              });
            }
          }
          if (
            (action.payload.action === WorkspacePresetAction.DELETE ||
              action.payload.action ===
                WorkspacePresetAction.DELETE_SYSTEM_PRESET) &&
            action.payload.workspaceId === draft.id
          ) {
            delete draft.id;
            draft.title = action.payload.emptyWorkspacePresetTitle;
            draft.abstract = getEmptyMapWorkspace().abstract;
            draft.scope = getEmptyMapWorkspace().scope;
            draft.username = undefined;
            draft.hasChanges = true;
            draft.username = '';
          }
        },
      )
      .addCase(viewPresetActions.fetchedViewPreset, (draft, action) => {
        const { panelId, viewPreset } = action.payload;
        const entity = draft.views.byId![panelId];

        if (entity) {
          Object.keys(viewPreset).forEach((key) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore  - For some reason the entity has a type of never, which prevents access with key
            entity[key as keyof WorkspaceViewType] =
              viewPreset[key as keyof ViewPreset]!;
          });
        }
      })
      .addCase(viewPresetActions.onSuccessViewPresetAction, (draft, action) => {
        const {
          panelId,
          activeViewPresetId,
          title,
          action: presetAction,
          scope,
          abstract,
          labels,
          username,
        } = action.payload;
        // If preset is not the active/selected one
        const shouldSkipInactivePresetUpdate =
          activeViewPresetId !== undefined &&
          activeViewPresetId !== action.payload.viewPresetId &&
          (presetAction === PresetAction.EDIT ||
            presetAction === PresetAction.SHARE ||
            (presetAction === PresetAction.SAVE_AS &&
              action.payload.shouldSetActiveViewPreset === false));
        if (shouldSkipInactivePresetUpdate) {
          return;
        }
        const entity = draft.views.byId![panelId];

        if (entity && presetAction !== PresetAction.DELETE) {
          entity.title = title;
          entity.scope = scope || 'user';
          entity.abstract = abstract;
          entity.description = action.payload.description;
          entity.labels = labels;
          entity.username = username;
        }
      });

    workspacePresetChangeActions.map((action) =>
      builder.addCase(action, (draft, payload) => {
        const isResetViewPreset =
          action === viewPresetActions.selectViewPreset &&
          (payload.payload as SelectViewPresetPayload)?.shouldReset;

        const isSyncGroupAction =
          action === syncGroupsActions.syncGroupAddGroup ||
          action === syncGroupsActions.syncGroupToggleIsTimeScrollingEnabled;

        const isUserOrigin =
          isSyncGroupAction &&
          (payload.payload as syncGroupsTypes.SyncGroupAddGroupPayload)
            ?.origin === 'user';

        const hasChanges =
          (!isResetViewPreset && !isSyncGroupAction) || isUserOrigin;
        draft.hasChanges = hasChanges;

        // reset title and scope when setting empty activeViewPresetId
        if (action.type === viewPresetActions.setActiveViewPresetId.type) {
          const { panelId, viewPresetId } =
            payload.payload as SetActiveViewPresetIdPayload;
          const entity = draft.views.byId![panelId];
          if (entity && viewPresetId === '') {
            entity.title = '';
            entity.scope = 'system';
            entity.abstract = '';
            entity.username = '';
            entity.labels = undefined;
          }
        }
      }),
    );
  },
});

export const workspaceReducer: Reducer<WorkspaceState> = slice.reducer;
export const workspaceActions = { ...slice.actions };
